// Freeform widget workspace — drag / resize / hide / restore, persisted
// to localStorage per user. Replaces the fixed `AnalysisLayout` from
// the first overhaul pass and the legacy `AnalysisShell` from Story 010.
//
// Christian's brief 2026-05-09 §"freeform widget canvas":
//   * the user wants drag + resize + hide,
//   * a settings button to bring hidden widgets back,
//   * persistence in browser cache so reopening the page restores the
//     workspace.
//
// Layout shape (12-col grid, rowHeight 30 px):
//
//   ┌──────┬─────────────┬──────────────────────┬──────────┐
//   │      │             │                      │          │
//   │Sketch│ List        │ Viewer               │ Filmstrip│
//   │ w=2  │ w=3         │ w=5 h=8              │ w=2      │
//   │ h=12 │ h=12        │                      │ h=12     │
//   │      │             │                      │          │
//   │      │             ├──────────────────────┤          │
//   │      │             │ Tiles  w=5 h=4       │          │
//   └──────┴─────────────┴──────────────────────┴──────────┘
//
// FilterToolbar stays *above* the workspace in `AnalysisFeature`, not in
// the grid — it's a navigation chrome surface, not a widget.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Responsive, WidthProvider, type Layout } from "react-grid-layout";
import { typography } from "@aeriosiq/ui";
import { ANALYSIS_PANEL_RADIUS } from "./AnalysisLayout.js";
import {
  MoveWidgetControl,
  usePopoutWorkspaceStore,
  outWidgetsOf,
} from "./multiwindow/index.js";
import { migrateLayoutsForCollision } from "./layoutMigration.js";
import {
  useAdvancedFilterWidgetStore,
  ADVANCED_FILTER_WIDGETS,
  type AdvancedFilterWidgetKey,
} from "./state/advancedFilterWidgetStore.js";
import { useHistogramWidgetStore } from "./histogram/histogramWidgetStore.js";
import { useWorkspaceUiStore } from "./state/workspaceUiStore.js";
import { GripVerticalIcon, RotateCcwIcon, XIcon } from "@aeriosiq/ui/icons";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

export type WidgetKey =
  | "sketch"
  | "list"
  | "viewer"
  | "tiles"
  | "filmstrip"
  // SB6-ANA-FILT-06 (D5) — the two advanced filter widgets. They are full
  // workspace widgets but start hidden and are surfaced on demand (L3 Kind
  // sliders icon / Settings → Layout & Widgets) into the first empty region.
  | "aiFilter"
  | "defectFilter"
  // Story 056 — the image histogram. Same on-demand pattern as the advanced
  // filter widgets (closed by default, surfaced from Settings → Layout &
  // Widgets), but its open state persists via `histogramWidgetStore`.
  | "histogram";

// Slot labels feed the workspace's per-tile <Paper role="region"
// aria-label={label}>. The `list` slot deliberately uses the bare
// "List" landmark so:
//   - `getByRole("region", { name: "List", exact: true })` (010/014
//     specs) matches the wrapper.
//   - `getByRole("region", { name: /findings/i })` (004/005/005a/
//     005b/007 specs) DOESN'T match the wrapper — it falls through to
//     the inner FindingsPanel's own role="region" aria-label="Findings".
//   - `getByRole("region", { name: /defects/i })` (006/008/029/031
//     specs) DOESN'T match the wrapper — it falls through to the inner
//     DefectsPanel's own role="region" aria-label="Defects".
// An earlier label "Defects · AI Findings" tripped strict-mode in
// both /findings/ and /defects/ regex lookups because the wrapper +
// the inner panel both matched.
const WIDGET_LABEL: Record<WidgetKey, string> = {
  sketch: "Component",
  list: "List",
  viewer: "Viewer",
  tiles: "Tiles",
  filmstrip: "Filmstrip",
  aiFilter: "AI Filter Module",
  defectFilter: "Defect Filter Widget",
  histogram: "Histogram",
};

const ALL_WIDGETS: readonly WidgetKey[] = [
  "sketch",
  "list",
  "viewer",
  "tiles",
  "filmstrip",
  "aiFilter",
  "defectFilter",
  "histogram",
];

// 12-col grid with rowHeight 30 → matches the ELEKS reference layout
// (Component left thin, Defects mid, Viewer dominant, Tiles under Viewer,
// Filmstrip right thin). Heights chosen so Viewer + Tiles together fill
// the same 12 rows as the side columns. Sm / xs collapse to vertical
// stacks since the grid logic doesn't read well on narrow screens.
const DEFAULT_LAYOUTS: Record<string, Layout[]> = {
  // Christian 2026-05-27 — the desktop (lg) platform default was updated to
  // his own arrangement: Filmstrip + Defects/AI list top-left, an enlarged
  // Viewer (w7, full height) dominating the right, Tiles below the list, and
  // the Component (sketch) widget HIDDEN by default (see DEFAULT_HIDDEN).
  // Sketch keeps a position here for when a user restores it from Settings.
  lg: [
    { i: "filmstrip", x: 0, y: 0, w: 2, h: 12, minW: 1, minH: 6 },
    { i: "list", x: 2, y: 0, w: 3, h: 12, minW: 2, minH: 6 },
    { i: "viewer", x: 5, y: 0, w: 7, h: 26, minW: 3, minH: 5 },
    { i: "tiles", x: 0, y: 12, w: 5, h: 14, minW: 3, minH: 4 },
    { i: "sketch", x: 0, y: 26, w: 2, h: 12, minW: 1, minH: 6 },
    // SB6-ANA-FILT-06 — fallback size/position for the advanced widgets.
    // They start hidden; when opened the workspace places them in the
    // first EMPTY region, falling back to "below the stack" using this
    // default size. The x/y here is only a default seed.
    { i: "aiFilter", x: 0, y: 38, w: 3, h: 10, minW: 2, minH: 6 },
    { i: "defectFilter", x: 3, y: 38, w: 3, h: 10, minW: 2, minH: 6 },
    // Story 056 — like the advanced widgets, this x/y is only a seed; the
    // histogram opens into the first EMPTY region.
    { i: "histogram", x: 6, y: 38, w: 3, h: 8, minW: 2, minH: 5 },
  ],
  md: [
    { i: "sketch", x: 0, y: 0, w: 2, h: 10, minW: 1, minH: 5 },
    { i: "list", x: 2, y: 0, w: 3, h: 10, minW: 2, minH: 5 },
    { i: "viewer", x: 5, y: 0, w: 4, h: 6, minW: 3, minH: 4 },
    { i: "tiles", x: 5, y: 6, w: 4, h: 4, minW: 3, minH: 3 },
    { i: "filmstrip", x: 9, y: 0, w: 1, h: 10, minW: 1, minH: 5 },
    { i: "aiFilter", x: 0, y: 10, w: 3, h: 8, minW: 2, minH: 5 },
    { i: "defectFilter", x: 3, y: 10, w: 3, h: 8, minW: 2, minH: 5 },
    { i: "histogram", x: 6, y: 10, w: 3, h: 7, minW: 2, minH: 5 },
  ],
  sm: [
    { i: "viewer", x: 0, y: 0, w: 6, h: 6 },
    { i: "tiles", x: 0, y: 6, w: 6, h: 3 },
    { i: "list", x: 0, y: 9, w: 4, h: 6 },
    { i: "sketch", x: 4, y: 9, w: 2, h: 6 },
    { i: "filmstrip", x: 0, y: 15, w: 6, h: 4 },
    { i: "aiFilter", x: 0, y: 19, w: 6, h: 6 },
    { i: "defectFilter", x: 0, y: 25, w: 6, h: 6 },
    { i: "histogram", x: 0, y: 31, w: 6, h: 6 },
  ],
  xs: [
    { i: "viewer", x: 0, y: 0, w: 4, h: 5 },
    { i: "tiles", x: 0, y: 5, w: 4, h: 3 },
    { i: "list", x: 0, y: 8, w: 4, h: 6 },
    { i: "sketch", x: 0, y: 14, w: 4, h: 4 },
    { i: "filmstrip", x: 0, y: 18, w: 4, h: 4 },
    { i: "aiFilter", x: 0, y: 22, w: 4, h: 6 },
    { i: "defectFilter", x: 0, y: 28, w: 4, h: 6 },
    { i: "histogram", x: 0, y: 34, w: 4, h: 5 },
  ],
};

// Christian 2026-05-27 — widgets hidden in the platform default (what a NEW
// user sees on first open, before personalising). The Component (sketch)
// widget starts hidden; users can restore it from Settings → Workspace.
// Existing users keep whatever they've already customised.
const DEFAULT_HIDDEN: Readonly<Partial<Record<WidgetKey, boolean>>> = {
  sketch: true,
  // SB6-ANA-FILT-06 (D5) — the advanced filter widgets are not part of the
  // default arrangement; they appear only when the analyst opens them.
  aiFilter: true,
  defectFilter: true,
};

const COLS = { lg: 12, md: 10, sm: 6, xs: 4 } as const;
// Breakpoints are matched against the RGL container's measured width
// (via `WidthProvider`), NOT the window width. Since Story 050 the
// chrome wraps every protected route — including `/analysis/:id` —
// with a 272 px always-expanded sidebar (bumped from 240 on 2026-05-12
// so the "Project Management" group label fits one line) + 24 px main
// padding either side + a possible 17 px vertical scrollbar. On the
// Playwright Desktop-Chrome 1280-viewport the analysis grid therefore
// measures ~943 px (1280 − 272 − 48 − 17). The previous `lg: 960`
// cutoff was calibrated against the 240 px sidebar; the 272 px bump
// pushed the measured width back below the lg threshold, dropping the
// grid into `md` and breaking 010 AC3 / AC4 / AC7. Cutoff lowered so:
//   - 1280-viewport with sidebar 272 lands in lg (~943 ≥ 880),
//   - 1280-viewport with sidebar collapsed (64) lands in lg (~1151),
//   - 768-viewport-ish tablet lands in md (≥ 680),
//   - 375-viewport mobile lands in xs (the chrome hides the sidebar
//     entirely below the `sm` media-query, see AppSidebar).
// DEFAULT_LAYOUTS per breakpoint are intentionally unchanged.
const BREAKPOINTS = { lg: 880, md: 680, sm: 480, xs: 0 } as const;
const ROW_HEIGHT = 30;
const MARGIN: [number, number] = [8, 8];
const DRAG_HANDLE_CLASS = "aero-widget-drag-handle";

// Storage split: layouts live under `aero.layout.analysis.${userId}` as the
// raw RGL `Record<string, Layout[]>` shape (Story 010 spec contract — that
// spec pre-seeds and reads this exact key). Hidden-widget state lives
// alongside under `aero.workspace.hidden.${userId}` so neither concern
// pollutes the other's persistence envelope.
function layoutsKey(userId: string): string {
  return `aero.layout.analysis.${userId}`;
}

function hiddenKey(userId: string): string {
  return `aero.workspace.hidden.${userId}`;
}

/**
 * R58-L-12 — strip RGL-private fields before serialising to
 * localStorage.
 *
 * `react-grid-layout` mutates layout entries in-place during drag /
 * resize — it sets `moved: true`, may carry `static: false`,
 * `isDraggable`, `isResizable`, `isBounded`, and a `placeholder` marker
 * through. None of those belong in the persisted envelope:
 *
 *   * with `compactType` now `null` (R58-L-12), a leaked `static: true`
 *     on a re-loaded entry would silently freeze a widget at its
 *     position;
 *   * `moved` + `placeholder` are per-gesture transient state that have
 *     no meaning across page loads;
 *   * keeping the persisted shape pinned to the documented 7-field
 *     contract (`i`, `x`, `y`, `w`, `h`, optional `minW`, optional
 *     `minH`) mirrors W-07's wire-shaping pattern so the local-only
 *     persistence path matches the dashboard's BE-persistence path
 *     and stays easy to reason about.
 *
 * Returns a new record with each entry pared down. Idempotent.
 */
function sanitiseLayouts(
  layouts: Record<string, Layout[]>,
): Record<string, Layout[]> {
  const cleanEntry = (l: Layout): Layout => {
    const out: Layout = {
      i: l.i,
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h,
    };
    if (typeof l.minW === "number") out.minW = l.minW;
    if (typeof l.minH === "number") out.minH = l.minH;
    return out;
  };
  const out: Record<string, Layout[]> = {};
  for (const [bp, arr] of Object.entries(layouts)) {
    out[bp] = arr.map(cleanEntry);
  }
  return out;
}

function loadLayouts(userId: string): Record<string, Layout[]> | null {
  try {
    const raw = window.localStorage.getItem(layoutsKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, Layout[]>;
  } catch {
    return null;
  }
}

function saveLayouts(userId: string, layouts: Record<string, Layout[]>): void {
  try {
    // R58-L-12 — sanitise BEFORE serialising; RGL's in-place mutation
    // would otherwise leak `moved` / `static` / `isDraggable` /
    // `placeholder` into persisted storage. See `sanitiseLayouts`.
    window.localStorage.setItem(
      layoutsKey(userId),
      JSON.stringify(sanitiseLayouts(layouts)),
    );
  } catch {
    // Quota or private mode — fail silently.
  }
}

function clearLayouts(userId: string): void {
  try {
    window.localStorage.removeItem(layoutsKey(userId));
  } catch {
    // ignore
  }
}

/** Returns the stored hidden-set, or null when the user has never stored one
 *  (so the caller can apply the platform default only to brand-new users). */
function loadHidden(
  userId: string,
): Partial<Record<WidgetKey, boolean>> | null {
  try {
    const raw = window.localStorage.getItem(hiddenKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as Partial<Record<WidgetKey, boolean>>;
  } catch {
    return null;
  }
}

function saveHidden(
  userId: string,
  hidden: Partial<Record<WidgetKey, boolean>>,
): void {
  try {
    window.localStorage.setItem(hiddenKey(userId), JSON.stringify(hidden));
  } catch {
    // ignore
  }
}

function clearHidden(userId: string): void {
  try {
    window.localStorage.removeItem(hiddenKey(userId));
  } catch {
    // ignore
  }
}

// SB6-ANA-POP-01 — bring-back slot reconciliation (Christian Q6a: "reuse
// the space where the widget was before → if the space is meanwhile used
// by another widget, just add it below all other widgets").
//
// `react-grid-layout` only reports VISIBLE children in `onLayoutChange`, so
// a popped-out widget's layout entry is dropped from state while it's out.
// We remember each widget's last on-grid cell (`slotMemoryRef`) so that on
// bring-back we can either restore it there (if still free) or append it
// below the stack (if another widget moved into its cell).

/** Axis-aligned rectangle overlap test on grid cells. */
function rectsOverlap(a: Layout, b: Layout): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  );
}

/** The default layout entry for a widget at a breakpoint (clamped to cols). */
function defaultEntryFor(key: WidgetKey, bp: string): Layout {
  const found = (DEFAULT_LAYOUTS[bp] ?? DEFAULT_LAYOUTS.lg ?? []).find(
    (l) => l.i === key,
  );
  if (found) return { ...found };
  return { i: key, x: 0, y: 0, w: 2, h: 6 };
}

/** A new full-default-span entry placed below every other (visible) widget. */
function appendBelowEntry(
  others: readonly Layout[],
  key: WidgetKey,
  bp: string,
): Layout {
  const cols = (COLS as Record<string, number | undefined>)[bp] ?? 12;
  const def = defaultEntryFor(key, bp);
  const stackBottom = others.reduce((acc, l) => Math.max(acc, l.y + l.h), 0);
  return { ...def, i: key, x: 0, y: stackBottom, w: Math.min(def.w, cols) };
}

/**
 * SB6-ANA-FILT-06 (D5) — find the first EMPTY grid region large enough to
 * hold a widget of size `w`×`h`, scanning top-to-bottom then
 * left-to-right. The scan is bounded vertically by the existing stack's
 * bottom plus `h` rows of headroom (so a widget always finds a home even
 * on a fully packed grid — it lands in the empty band below). Pure +
 * exported for unit testing.
 *
 * Contract (Christian, D5): "add the widget into the first EMPTY region of
 * the grid-canvas; never reposition widgets already visible — only fill
 * empty space." This returns a cell that does NOT overlap any
 * `visibleOthers`; the caller leaves every other widget untouched.
 */
export function firstEmptyRegion(args: {
  readonly cols: number;
  readonly w: number;
  readonly h: number;
  /** Cells of the widgets currently on the grid. */
  readonly visibleOthers: readonly Layout[];
}): { x: number; y: number } {
  const { cols, visibleOthers } = args;
  const w = Math.min(args.w, cols);
  const h = Math.max(1, args.h);
  const stackBottom = visibleOthers.reduce(
    (acc, l) => Math.max(acc, l.y + l.h),
    0,
  );
  // Scan rows from the top down to one full widget-height past the stack
  // bottom (inclusive) so there is always a free band to land in.
  const maxY = stackBottom + h;
  for (let y = 0; y <= maxY; y += 1) {
    for (let x = 0; x + w <= cols; x += 1) {
      const candidate: Layout = { i: "__probe", x, y, w, h };
      const overlaps = visibleOthers.some((l) => rectsOverlap(candidate, l));
      if (!overlaps) return { x, y };
    }
  }
  // Unreachable given the maxY headroom, but keep a safe fallback.
  return { x: 0, y: stackBottom };
}

/**
 * SB6-ANA-FILT-06 (D5) — produce the layout entry for a widget being
 * surfaced into the grid, placed in the first empty region. Pure +
 * exported for unit testing.
 */
export function placeWidgetInEmptyRegion(args: {
  readonly key: WidgetKey;
  readonly bp: string;
  readonly cols: number;
  readonly visibleOthers: readonly Layout[];
}): Layout {
  const def = defaultEntryFor(args.key, args.bp);
  const w = Math.min(def.w, args.cols);
  const h = def.h;
  const { x, y } = firstEmptyRegion({
    cols: args.cols,
    w,
    h,
    visibleOthers: args.visibleOthers,
  });
  return {
    ...def,
    i: args.key,
    x,
    y,
    w,
  };
}

/**
 * SB6-ANA-POP-01 (Q6a) — decide where a brought-back widget goes:
 *   - back to its `remembered` cell if that cell is still free,
 *   - else appended below every other visible widget.
 * Pure + exported for unit testing.
 */
export function placeBroughtBackWidget(args: {
  readonly key: WidgetKey;
  readonly bp: string;
  /** The widget's last on-grid cell, if we remember it. */
  readonly remembered: Layout | undefined;
  /** Cells of the widgets currently on the grid (excluding the returning one). */
  readonly visibleOthers: readonly Layout[];
}): Layout {
  const desired = args.remembered ?? defaultEntryFor(args.key, args.bp);
  const occupied = args.visibleOthers.some((l) => rectsOverlap(l, desired));
  return occupied
    ? appendBelowEntry(args.visibleOthers, args.key, args.bp)
    : { ...desired, i: args.key };
}

interface WidgetSlotProps {
  widgetKey: WidgetKey;
  onHide: (key: WidgetKey) => void;
  onKeyboardNudge: (key: WidgetKey, dx: number, dy: number) => void;
  layoutEntry: Layout | undefined;
  children: React.ReactNode;
  /** Injected by RGL via React.cloneElement. */
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
}

const WidgetSlot = ({
  widgetKey,
  onHide,
  onKeyboardNudge,
  layoutEntry,
  children,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
}: WidgetSlotProps): React.ReactElement => {
  const label = WIDGET_LABEL[widgetKey];

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    let dx = 0;
    let dy = 0;
    switch (event.key) {
      case "ArrowLeft":
        dx = -1;
        break;
      case "ArrowRight":
        dx = 1;
        break;
      case "ArrowUp":
        dy = -1;
        break;
      case "ArrowDown":
        dy = 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    onKeyboardNudge(widgetKey, dx, dy);
  };

  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label={label}
      // Story 010 AC4 + AC7 read x/y back from these attributes — the
      // RGL transform that positions the tile isn't directly assertable.
      data-grid-x={layoutEntry?.x ?? null}
      data-grid-y={layoutEntry?.y ?? null}
      data-grid-w={layoutEntry?.w ?? null}
      data-grid-h={layoutEntry?.h ?? null}
      // Story 063 — focused-widget detection for the global
      // Ctrl+Shift+W binding (useWidgetPopoutHotkey).
      data-widget-key={widgetKey}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        borderRadius: `${String(ANALYSIS_PANEL_RADIUS)}px`,
      }}
      {...(style ? { style } : {})}
      {...(className ? { className } : {})}
      {...(onMouseDown ? { onMouseDown } : {})}
      {...(onMouseUp ? { onMouseUp } : {})}
      {...(onTouchEnd ? { onTouchEnd } : {})}
    >
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          px: 1,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Tooltip title="Drag to move">
          <Box
            className={DRAG_HANDLE_CLASS}
            data-testid="widget-drag-handle"
            role="button"
            aria-label={`Move ${label}`}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              cursor: "move",
              color: "text.secondary",
              borderRadius: 0.5,
              outline: "none",
              "&:focus-visible": {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
            }}
          >
            <GripVerticalIcon fontSize="small" />
          </Box>
        </Tooltip>
        <Typography
          component="h2"
          sx={{
            flexGrow: 1,
            fontSize: typography.size["label-md"],
            fontWeight: typography.weight.semibold,
            letterSpacing: typography.tracking["label-md"],
            color: "text.primary",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        {/* SB6-ANA-POP-04 — all workspace widgets (incl. the two advanced
            filter widgets) are now poppable, so each header carries the
            move-to-window control. `WidgetKey` ⊆ `PopoutWidgetKey`. */}
        <MoveWidgetControl widget={widgetKey} />
        <Tooltip title={`Hide ${label}`}>
          <IconButton
            size="small"
            onClick={() => {
              onHide(widgetKey);
            }}
            aria-label={`Hide ${label}`}
            sx={{ width: 24, height: 24 }}
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>{children}</Box>
    </Paper>
  );
};

interface SettingsDialogProps {
  open: boolean;
  hidden: Partial<Record<WidgetKey, boolean>>;
  onClose: () => void;
  onRestore: (key: WidgetKey) => void;
  onResetLayout: () => void;
}

function SettingsDialog({
  open,
  hidden,
  onClose,
  onRestore,
  onResetLayout,
}: SettingsDialogProps): React.ReactElement {
  const hiddenKeys = ALL_WIDGETS.filter((k) => hidden[k] === true);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Workspace</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mb: 1 }}
        >
          Drag widget headers to move, drag corners to resize, click ✕ to hide.
          Your layout is saved per browser.
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ mt: 2, mb: 0.5, color: "text.primary" }}
        >
          Hidden widgets
        </Typography>
        {hiddenKeys.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            All widgets are visible.
          </Typography>
        ) : (
          <List dense disablePadding>
            {hiddenKeys.map((k) => (
              <ListItem
                key={k}
                disableGutters
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      onRestore(k);
                    }}
                  >
                    Restore
                  </Button>
                }
              >
                <ListItemText primary={WIDGET_LABEL[k]} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<RotateCcwIcon />}
          onClick={onResetLayout}
          color="inherit"
        >
          Reset layout
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface AnalysisWorkspaceProps {
  userId: string;
  /** Map widget key → body content. Order is fixed by ALL_WIDGETS. */
  widgets: Record<WidgetKey, React.ReactNode>;
}

export function AnalysisWorkspace({
  userId,
  widgets,
}: AnalysisWorkspaceProps): React.ReactElement {
  const [layouts, setLayouts] = useState<Record<string, Layout[]>>(() => {
    const stored = loadLayouts(userId);
    if (!stored) return DEFAULT_LAYOUTS;
    // SB6-ANA-LAYOUT-01 — under hard-edge collision the saved layout
    // must be free of overlaps before RGL takes it. Any rounding-edge
    // overlap left over from the cascading-push era is swept to the
    // nearest free cell here. Per Christian's spec (option a) we also
    // write the cleaned layout back to localStorage so the migration
    // cost is paid once per browser. The write happens lazily in an
    // effect below (initial state must stay pure).
    const { layouts: cleaned } = migrateLayoutsForCollision(stored, COLS);
    return cleaned;
  });
  const [hidden, setHidden] = useState<Partial<Record<WidgetKey, boolean>>>(
    () => {
      const storedHidden = loadHidden(userId);
      if (storedHidden) return storedHidden;
      // No hidden state stored. A brand-new user (no stored LAYOUT either)
      // gets the platform default-hidden set (Component hidden); an existing
      // user who has a layout but never hid anything keeps everything visible
      // (don't retroactively hide a widget on them).
      return loadLayouts(userId) === null ? { ...DEFAULT_HIDDEN } : {};
    },
  );
  const [breakpoint, setBreakpoint] = useState<string>("lg");
  // Story 036 — the gear's settings panel is now the right-side
  // `<SettingsDrawer/>` mounted as a sibling of this workspace. The
  // legacy SettingsDialog below (a centered modal) is kept for the
  // imperative "Reset layout" path but is no longer wired to the
  // workspace UI store — opening from the gear must NOT show it.
  const [legacyDialogOpen, setLegacyDialogOpen] = useState(false);

  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    const stored = loadLayouts(userId);
    if (stored === null) {
      saveLayouts(userId, DEFAULT_LAYOUTS);
      return;
    }
    // SB6-ANA-LAYOUT-01 — persist the migrated layout if the load-time
    // sweep moved anything. The constructor of `layouts` already swept
    // it; here we just check the original-vs-cleaned diff so we don't
    // overwrite localStorage on every reload.
    const { layouts: cleaned, changed } = migrateLayoutsForCollision(
      stored,
      COLS,
    );
    if (changed) saveLayouts(userId, cleaned);
  }, [userId]);

  // SB6-ANA-POP-02 — hydrate the per-user pop-out WORKSPACE (which widgets
  // live in which window) from sessionStorage. Survives DA navigation in the
  // same session.
  const hydrateWorkspace = usePopoutWorkspaceStore((s) => s.hydrate);
  useEffect(() => {
    hydrateWorkspace(userId);
  }, [hydrateWorkspace, userId]);

  // SB6-ANA-POP-02 — widgets that live in a pop-out window disappear from the
  // in-page grid; the remaining widgets re-flow into the freed space. When a
  // widget returns to the main grid the reconcile effect below restores its
  // slot (reuse cell, else append below). `outWidgetsOf` derives the set from
  // the window model; the useMemo keeps the Set reference stable.
  const popoutWindows = usePopoutWorkspaceStore((s) => s.windows);
  const poppedOutWidgets = useMemo(
    () => outWidgetsOf(popoutWindows),
    [popoutWindows],
  );
  // SB6-ANA-FILT-06 — a string-keyed view of the popped-out set so the
  // grid reconciliation can test `WidgetKey`s (incl. the advanced filter
  // widgets, which are never poppable) without a type cast at each call.
  const poppedOutKeys = useMemo<Set<string>>(
    () => new Set<string>(poppedOutWidgets),
    [poppedOutWidgets],
  );

  // SB6-ANA-POP-01 — last on-grid cell per widget (per breakpoint), and the
  // previous popped-out set, so we can reconcile a brought-back widget's
  // slot (reuse if free, else append below — Q6a).
  const slotMemoryRef = useRef<
    Partial<Record<WidgetKey, Record<string, Layout>>>
  >({});
  // Stored as plain strings: `poppedOutWidgets` is keyed by `PopoutWidgetKey`
  // (7 keys), a superset of the 5 in-grid `WidgetKey`s.
  const prevPoppedRef = useRef<Set<string>>(new Set());

  const handleLayoutChange = useCallback(
    (_current: Layout[], all: Record<string, Layout[]>) => {
      setLayouts(all);
      saveLayouts(userId, all);
    },
    [userId],
  );

  // SB6-ANA-FILT-06 (D4) — closing an advanced filter widget from its
  // header ✕ must also flip the shared store off so the L3 Kind button's
  // open-state and the Settings checkbox stay in lockstep.
  const closeAdvanced = useAdvancedFilterWidgetStore((s) => s.close);
  // Story 056 — the histogram widget's ✕ closes it through its own store
  // (persisted), mirroring the advanced-widget close path. `open` drives
  // grid visibility; `openTicket` drives first-empty-region placement;
  // `placedTicket` (store-held, NOT a per-mount ref) records which ticket
  // has already been placed so a workspace remount never re-places.
  const setHistogramOpen = useHistogramWidgetStore((s) => s.setOpen);
  const histogramOpen = useHistogramWidgetStore((s) => s.open);
  const histogramTicket = useHistogramWidgetStore((s) => s.openTicket);
  const histogramPlacedTicket = useHistogramWidgetStore((s) => s.placedTicket);
  const markHistogramPlaced = useHistogramWidgetStore((s) => s.markPlaced);
  // SB6-ANA-FILT-06 — advanced-widget visibility, read early because the
  // bring-back reconciliation below must know whether a CLOSED advanced
  // widget's layout entry actually occupies its cell (it doesn't).
  const advVisible = useAdvancedFilterWidgetStore((s) => s.visible);
  const advTickets = useAdvancedFilterWidgetStore((s) => s.openTicket);
  const handleHide = useCallback(
    (key: WidgetKey) => {
      if (key === "histogram") {
        setHistogramOpen(false);
        return;
      }
      if (ADVANCED_FILTER_WIDGETS.includes(key as AdvancedFilterWidgetKey)) {
        closeAdvanced(key as AdvancedFilterWidgetKey);
        return; // the reconcile effect flips `hidden` from the store
      }
      setHidden((prev) => {
        const next = { ...prev, [key]: true };
        saveHidden(userId, next);
        return next;
      });
    },
    [userId, closeAdvanced, setHistogramOpen],
  );

  const handleRestore = useCallback(
    (key: WidgetKey) => {
      setHidden((prev) => {
        // Filter the key out instead of dynamic-delete — the eslint
        // rule `no-dynamic-delete` flags `delete obj[variable]` as a
        // de-opt + readability hazard. Same semantics, plain syntax.
        const next: Partial<Record<WidgetKey, boolean>> = {};
        for (const k of Object.keys(prev) as WidgetKey[]) {
          if (k === key) continue;
          const v = prev[k];
          if (v !== undefined) next[k] = v;
        }
        saveHidden(userId, next);
        return next;
      });
    },
    [userId],
  );

  const handleResetLayout = useCallback(() => {
    clearLayouts(userId);
    clearHidden(userId);
    setLayouts(DEFAULT_LAYOUTS);
    // Reset to the platform default-hidden set (Component hidden), not "all
    // visible", so "Reset layout" reproduces the first-open experience.
    setHidden({ ...DEFAULT_HIDDEN });
    // Story 056 — the histogram defaults to CLOSED; both reset paths
    // (this imperative reset and the Layout tab's "Reset this tab") must
    // agree, so close it here too.
    setHistogramOpen(false);
  }, [userId, setHistogramOpen]);

  // SB6-ANA-HEADER-02 — the reset-layout icon moved off the canvas into
  // Settings → Layout & Widgets. The reset is imperative + owns local
  // state + per-user localStorage, so we publish `handleResetLayout` into
  // the workspace UI store for the SettingsModal to invoke; the behavior
  // stays byte-identical.
  const registerResetLayout = useWorkspaceUiStore((s) => s.registerResetLayout);
  useEffect(() => {
    registerResetLayout(handleResetLayout);
    return () => {
      registerResetLayout(null);
    };
  }, [registerResetLayout, handleResetLayout]);

  // SB6-ANA-POP-01 — remember each visible widget's current cell so a later
  // bring-back can restore it. Skips popped-out widgets (their entry is
  // gone from `layouts` while out; we keep the last on-grid value).
  useEffect(() => {
    for (const [bp, arr] of Object.entries(layouts)) {
      for (const l of arr) {
        const key = l.i as WidgetKey;
        if (poppedOutKeys.has(key)) continue;
        const mem = slotMemoryRef.current[key] ?? {};
        mem[bp] = {
          i: l.i,
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
          ...(typeof l.minW === "number" ? { minW: l.minW } : {}),
          ...(typeof l.minH === "number" ? { minH: l.minH } : {}),
        };
        slotMemoryRef.current[key] = mem;
      }
    }
  }, [layouts, poppedOutKeys]);

  // SB6-ANA-POP-01 — when a widget transitions from popped-out back to
  // in-page, reconcile its grid slot: reuse its remembered cell if still
  // free, otherwise append it below all other widgets (Christian Q6a).
  useEffect(() => {
    const prev = prevPoppedRef.current;
    const current = poppedOutKeys;
    const broughtBack: WidgetKey[] = [];
    for (const k of prev) {
      // Only reconcile in-grid widgets (a brought-back `viewer` etc.); the
      // popped set may also name non-grid keys (navigator/catalogue).
      if (!current.has(k) && ALL_WIDGETS.includes(k as WidgetKey)) {
        broughtBack.push(k as WidgetKey);
      }
    }
    prevPoppedRef.current = new Set<string>(current);
    if (broughtBack.length === 0) return;

    setLayouts((prevLayouts) => {
      const next: Record<string, Layout[]> = {};
      const bps = new Set([
        ...Object.keys(prevLayouts),
        ...Object.keys(DEFAULT_LAYOUTS),
      ]);
      for (const bp of bps) {
        let arr = (prevLayouts[bp] ?? DEFAULT_LAYOUTS[bp] ?? []).slice();
        for (const key of broughtBack) {
          const others = arr.filter((l) => l.i !== key);
          // Only count widgets that are actually on the grid right now —
          // not hidden, not still popped, not store-closed — when testing
          // for a free cell. The advanced filter widgets and the histogram
          // are governed by their stores, NOT the `hidden` map (a closed
          // one has a layout SEED entry but occupies no cell).
          const visibleOthers = others.filter((l) => {
            const wk = l.i as WidgetKey;
            if (current.has(wk)) return false;
            if (wk === "histogram") return histogramOpen;
            if (
              ADVANCED_FILTER_WIDGETS.includes(wk as AdvancedFilterWidgetKey)
            ) {
              return advVisible[wk as AdvancedFilterWidgetKey];
            }
            return hidden[wk] !== true;
          });
          const placed = placeBroughtBackWidget({
            key,
            bp,
            remembered: slotMemoryRef.current[key]?.[bp],
            visibleOthers,
          });
          arr = [...others, placed];
        }
        next[bp] = arr;
      }
      saveLayouts(userId, next);
      return next;
    });
  }, [poppedOutKeys, hidden, userId, histogramOpen, advVisible]);

  // SB6-ANA-FILT-06 (D5) — place an advanced filter widget into the first
  // EMPTY grid region whenever it is opened. The dedicated store
  // (`useAdvancedFilterWidgetStore`) owns these widgets' VISIBILITY (see
  // `visibleKeys`, selectors read above the bring-back effect); this
  // effect only assigns a grid cell on each OPEN (false→true transition,
  // or an explicit re-open tracked via `openTicket`) — never
  // repositioning any widget already on the grid.
  const prevAdvTicketsRef = useRef<Record<AdvancedFilterWidgetKey, number>>({
    aiFilter: 0,
    defectFilter: 0,
  });
  useEffect(() => {
    for (const key of ADVANCED_FILTER_WIDGETS) {
      const opened =
        advVisible[key] && advTickets[key] !== prevAdvTicketsRef.current[key];
      if (!opened) continue;
      // Place it into the first empty region at every breakpoint.
      setLayouts((prevLayouts) => {
        const next: Record<string, Layout[]> = {};
        const bps = new Set([
          ...Object.keys(prevLayouts),
          ...Object.keys(DEFAULT_LAYOUTS),
        ]);
        for (const bp of bps) {
          const arr = (prevLayouts[bp] ?? DEFAULT_LAYOUTS[bp] ?? []).slice();
          const others = arr.filter((l) => l.i !== key);
          const cols = (COLS as Record<string, number | undefined>)[bp] ?? 12;
          // Only count widgets that are actually visible on the grid right
          // now when testing for a free region (hidden base widgets, popped
          // widgets, and not-yet-opened advanced widgets don't occupy space).
          const visibleOthers = others.filter((l) => {
            const wk = l.i as WidgetKey;
            if (poppedOutKeys.has(wk)) return false;
            if (wk === "histogram") return histogramOpen;
            if (
              ADVANCED_FILTER_WIDGETS.includes(wk as AdvancedFilterWidgetKey)
            ) {
              return advVisible[wk as AdvancedFilterWidgetKey];
            }
            return hidden[wk] !== true;
          });
          const placed = placeWidgetInEmptyRegion({
            key,
            bp,
            cols,
            visibleOthers,
          });
          next[bp] = [...others, placed];
        }
        saveLayouts(userId, next);
        return next;
      });
    }
    prevAdvTicketsRef.current = { ...advTickets };
  }, [advVisible, advTickets, hidden, poppedOutKeys, userId, histogramOpen]);

  // Story 056 — surface the histogram into the first EMPTY grid region on
  // each explicit open, mirroring the advanced-filter placement effect
  // above (never repositioning widgets already on the grid).
  //
  // The already-placed marker lives IN THE STORE (`placedTicket`), not in
  // a per-mount ref: `openTicket` survives navigation (module-singleton
  // store) while a ref would restart at 0 on every workspace remount and
  // make this effect re-place — yanking the widget back to "first empty
  // region" and overwriting the user's dragged position on every re-entry
  // into the analysis surface. Placement fires exactly once per OPEN
  // action.
  useEffect(() => {
    const opened = histogramOpen && histogramTicket !== histogramPlacedTicket;
    if (!opened) return;
    markHistogramPlaced(histogramTicket);
    setLayouts((prevLayouts) => {
      const next: Record<string, Layout[]> = {};
      const bps = new Set([
        ...Object.keys(prevLayouts),
        ...Object.keys(DEFAULT_LAYOUTS),
      ]);
      for (const bp of bps) {
        const arr = (prevLayouts[bp] ?? DEFAULT_LAYOUTS[bp] ?? []).slice();
        const others = arr.filter((l) => l.i !== "histogram");
        const cols = (COLS as Record<string, number | undefined>)[bp] ?? 12;
        const visibleOthers = others.filter((l) => {
          const wk = l.i as WidgetKey;
          if (poppedOutKeys.has(wk)) return false;
          if (ADVANCED_FILTER_WIDGETS.includes(wk as AdvancedFilterWidgetKey)) {
            return advVisible[wk as AdvancedFilterWidgetKey];
          }
          return hidden[wk] !== true;
        });
        const placed = placeWidgetInEmptyRegion({
          key: "histogram",
          bp,
          cols,
          visibleOthers,
        });
        next[bp] = [...others, placed];
      }
      saveLayouts(userId, next);
      return next;
    });
  }, [
    histogramOpen,
    histogramTicket,
    histogramPlacedTicket,
    markHistogramPlaced,
    advVisible,
    hidden,
    poppedOutKeys,
    userId,
  ]);

  const handleKeyboardNudge = useCallback(
    (key: WidgetKey, dx: number, dy: number) => {
      setLayouts((prev) => {
        const bp = breakpoint in prev ? breakpoint : "lg";
        const arr = prev[bp] ?? DEFAULT_LAYOUTS[bp] ?? DEFAULT_LAYOUTS.lg ?? [];
        const cols = COLS[bp as keyof typeof COLS];
        const next = arr.map((entry) => {
          if (entry.i !== key) return entry;
          const nextX = Math.max(0, Math.min(cols - entry.w, entry.x + dx));
          const nextY = Math.max(0, entry.y + dy);
          return { ...entry, x: nextX, y: nextY };
        });
        const all = { ...prev, [bp]: next };
        saveLayouts(userId, all);
        return all;
      });
    },
    [breakpoint, userId],
  );

  const visibleKeys = useMemo(
    () =>
      ALL_WIDGETS.filter((k) => {
        if (poppedOutKeys.has(k)) return false;
        // Story 056 — the histogram is governed by its own persisted store
        // (closed by default; an existing user has no `hidden` entry for
        // it, so the `hidden` map's absent-means-visible default would
        // wrongly surface it).
        if (k === "histogram") return histogramOpen;
        // SB6-ANA-FILT-06 (D5) — the advanced filter widgets are governed
        // by their dedicated store, NOT the localStorage `hidden` map (an
        // existing user has no `hidden` entry for them, so they must never
        // appear unless the store says they're open).
        if (ADVANCED_FILTER_WIDGETS.includes(k as AdvancedFilterWidgetKey)) {
          return advVisible[k as AdvancedFilterWidgetKey];
        }
        return hidden[k] !== true;
      }),
    [hidden, poppedOutKeys, advVisible, histogramOpen],
  );

  // Filter the layouts the grid sees down to visible widgets only —
  // RGL ignores layout entries with no matching child but emits a
  // warning. Easier to just slice the layouts.
  //
  // Back-fill: if a stored layout is missing an entry for a visible
  // widget (e.g. an older browser cache OR a test fixture that seeded
  // only a subset of widgets, see 010 AC4), splice in a non-colliding
  // entry below the existing stack. Without this, RGL's
  // `synchronizeLayoutWithChildren` auto-places the missing widget at
  // x:0, y:bottom(layout)=0 (since synchronize evaluates `bottom` on
  // the in-progress layout array, NOT the initial) which collides
  // with whatever the seed put at (0,0). The collision-push logic
  // (RGL's default with `allowOverlap: false`) then displaces the
  // seeded widget by one row, breaking any assertion that pins the
  // seed's y position. R58-L-12 flipped `compactType` to `null`, but
  // the back-fill is still load-bearing — collision push survives even
  // without compaction, so a colliding auto-placement still shifts the
  // seed.
  const filteredLayouts = useMemo(() => {
    const out: Record<string, Layout[]> = {};
    const knownBps = new Set([
      ...Object.keys(layouts),
      ...Object.keys(DEFAULT_LAYOUTS),
    ]);
    for (const bp of knownBps) {
      const stored = (layouts[bp] ?? []).filter((l) =>
        visibleKeys.includes(l.i as WidgetKey),
      );
      const cols = (COLS as Record<string, number | undefined>)[bp] ?? 12;
      const storedIds = new Set(stored.map((l) => l.i));
      if (stored.length === 0) {
        // No stored layout at this breakpoint — fall through to the
        // full default for this breakpoint.
        out[bp] = (DEFAULT_LAYOUTS[bp] ?? DEFAULT_LAYOUTS.lg ?? []).filter(
          (l) => visibleKeys.includes(l.i as WidgetKey),
        );
        continue;
      }
      // Partial seed: append any missing visible widgets below the
      // existing stack, full-width, so the seed's positions are not
      // shifted by RGL's auto-placement.
      const stackBottom = stored.reduce(
        (acc, l) => Math.max(acc, l.y + l.h),
        0,
      );
      let nextY = stackBottom;
      const missing: Layout[] = [];
      for (const wk of ALL_WIDGETS) {
        if (!visibleKeys.includes(wk)) continue;
        if (storedIds.has(wk)) continue;
        const defaultEntry = (DEFAULT_LAYOUTS[bp] ?? []).find(
          (l) => l.i === wk,
        );
        const w = Math.min(defaultEntry?.w ?? 1, cols);
        const h = defaultEntry?.h ?? 4;
        const minH = defaultEntry?.minH;
        const minW = defaultEntry?.minW;
        missing.push({
          i: wk,
          x: 0,
          y: nextY,
          w,
          h,
          ...(minW !== undefined ? { minW } : {}),
          ...(minH !== undefined ? { minH } : {}),
        });
        nextY += h;
      }
      out[bp] = [...stored, ...missing];
    }
    return out;
  }, [layouts, visibleKeys]);

  const currentBpLayout = useMemo(() => {
    const bp = breakpoint in layouts ? breakpoint : "lg";
    return layouts[bp] ?? DEFAULT_LAYOUTS[bp] ?? DEFAULT_LAYOUTS.lg ?? [];
  }, [breakpoint, layouts]);

  return (
    <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      {/* SB6-ANA-HEADER-02 — the standalone "Reset analysis layout" icon
          row is gone; the reset now lives in Settings → Layout & Widgets
          (wired via the workspace UI store's registered handler above). */}
      <ResponsiveGridLayout
        className="aero-analysis-workspace"
        layouts={filteredLayouts}
        breakpoints={BREAKPOINTS}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        margin={MARGIN}
        draggableHandle={`.${DRAG_HANDLE_CLASS}`}
        onLayoutChange={handleLayoutChange}
        onBreakpointChange={(bp) => {
          setBreakpoint(bp);
        }}
        // R58-L-12 — `compactType={null}` removes auto-compaction so a
        // user can park a widget in a bottom-right cell with empty
        // space above it without RGL pulling it adjacent to a
        // neighbour.
        compactType={null}
        // SB6-ANA-LAYOUT-01 — hard-edge collision. Christian's review
        // ("its not working as it is for now - we always need to reset
        // to default as it is impossible to arrange the widgets"): the
        // cascading-push behaviour made any drag/resize cascade into
        // adjacent widgets, leaving the analyst no way to build a
        // usable arrangement. With `preventCollision={true}`:
        //   - resize stops at the first occupied edge (no cascade),
        //   - a drag whose destination would overlap a neighbour
        //     reverts (RGL snaps the widget back to its origin).
        // `allowOverlap` stays false so the no-cascade rule is real.
        // Saved layouts containing accidental overlaps are migrated to
        // the nearest valid configuration on load — see
        // `migrateLayoutsForCollision` and OQ-SB6-D2 (option a).
        preventCollision={true}
      >
        {visibleKeys.map((key) => {
          const layoutEntry = currentBpLayout.find((l) => l.i === key);
          return (
            <div key={key}>
              <WidgetSlot
                widgetKey={key}
                onHide={handleHide}
                onKeyboardNudge={handleKeyboardNudge}
                layoutEntry={layoutEntry}
              >
                {widgets[key]}
              </WidgetSlot>
            </div>
          );
        })}
      </ResponsiveGridLayout>
      <SettingsDialog
        open={legacyDialogOpen}
        hidden={hidden}
        onClose={() => {
          setLegacyDialogOpen(false);
        }}
        onRestore={handleRestore}
        onResetLayout={handleResetLayout}
      />
      {/*
        Aside on `breakpoint`: RGL reports it via `onBreakpointChange`.
        Today we only use it implicitly (RGL picks the right entry from
        `filteredLayouts`); kept the state hook so future per-breakpoint
        editing UI can read it without re-deriving from window width.
      */}
      <span hidden aria-hidden data-breakpoint={breakpoint} />
    </Box>
  );
}
